import { useState, useMemo, useEffect, useCallback } from 'react';
import { InterfaceType, ManualDeploymentStatus } from '@/constant';
import { DataGrid, KeenIcon, Container } from '@/components';
import { ConfirmationDialog } from '@/components/dialogBox';
import usePagination from '@/hooks/usePagination';
import {
  useGetAllDeploymentsQuery,
  useConfirmDeploymentMutation,
} from '@/services/DeploymentService';
import { IDeploymentData } from '@/types/deployement-management.types';
import { useNavigate } from 'react-router-dom';
import { Toolbar, ToolbarHeading } from '@/layouts/demo1/toolbar';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/Date';

const DeploymentsListing = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const [confirmActionState, setConfirmActionState] = useState<{
    isOpen: boolean;
    deploymentId: string | null;
    action: 'confirm' | 'decline' | null;
    release_notes?: string;
    release_notes_url?: string;
    attachment?: File | null;
  }>({ isOpen: false, deploymentId: null, action: null, attachment: null });

  const [interfaceFilter, setInterfaceFilter] = useState<string>('all');

  const getInterfaceType = (filter: string): number | undefined => {
    const map: { [key: string]: number } = {
      admin: InterfaceType.ADMIN,
      frontend: InterfaceType.FRONTEND,
      backend: InterfaceType.BACKEND,
    };
    return filter !== 'all' ? map[filter] : undefined;
  };

  const getInterfaceLabel = (type?: number) => {
    if (type === InterfaceType.ADMIN) return 'Admin';
    if (type === InterfaceType.FRONTEND) return 'Frontend';
    if (type === InterfaceType.BACKEND) return 'Backend';
    return '-';
  };

  const queryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    interface_type: getInterfaceType(interfaceFilter),
  };

  const { data, isLoading, isFetching, isError, refetch } =
    useGetAllDeploymentsQuery(queryParams);

  // Separate queries for current version cards
  const { data: adminData } = useGetAllDeploymentsQuery({
    page: 1,
    limit: 1,
    interface_type: InterfaceType.ADMIN,
  });

  const { data: frontendData } = useGetAllDeploymentsQuery({
    page: 1,
    limit: 1,
    interface_type: InterfaceType.FRONTEND,
  });

  const { data: backendData } = useGetAllDeploymentsQuery({
    page: 1,
    limit: 1,
    interface_type: InterfaceType.BACKEND,
  });

  const [confirmDeployment, { isLoading: isConfirming }] =
    useConfirmDeploymentMutation();

  useEffect(() => {
    if (data) {
      handleTotalPages(data.totalPages || 1);
      handleTotalItems(data.totalItems || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (typeof refetch === 'function') {
      refetch();
    }
  }, [interfaceFilter, debouncedValue, pagination, refetch]);

  const filteredData = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map((deployment) => ({
      id: deployment.id,
      version: deployment.version,
      deployed_at: deployment.deployed_at,
      confirmed_at: deployment.confirmed_at,
      confirmed_by: deployment.confirmed_by,
      declined_at: deployment.declined_at,
      declined_reason: deployment.declined_reason,
      release_notes: deployment.release_notes,
      release_notes_url: deployment.release_notes_url,
      interface_type: deployment.interface_type,
      metadata: deployment.metadata,
    }));
  }, [data]);

  const handleActionClick = (
    deploymentId: string,
    action: 'confirm' | 'decline'
  ) => {
    setConfirmActionState({
      isOpen: true,
      deploymentId,
      action,
      release_notes: '',
      release_notes_url: '',
      attachment: null,
    });
  };

  const handleViewClick = useCallback(
    (deploymentId: string) => {
      navigate(`/deployments/${deploymentId}`);
    },
    [navigate]
  );

  const handleConfirmAction = async () => {
    if (confirmActionState.deploymentId && confirmActionState.action) {
      try {
        const status = confirmActionState.action === 'confirm' ? 1 : 2;
        await confirmDeployment({
          id: confirmActionState.deploymentId,
          update: {
            release_notes: confirmActionState.release_notes || undefined,
            status,
            attachment: confirmActionState.attachment || undefined,
            release_notes_url:
              confirmActionState.release_notes_url || undefined,
          },
        }).unwrap();
        if (typeof refetch === 'function') {
          refetch();
        }
        setConfirmActionState({
          isOpen: false,
          deploymentId: null,
          action: null,
          release_notes: undefined,
          release_notes_url: undefined,
          attachment: null,
        });
      } catch (error) {
        console.error('Failed to update deployment:', error);
      }
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setConfirmActionState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setConfirmActionState((prev) => ({ ...prev, attachment: file }));
    }
  };

  const resetFilters = () => {
    resetSearch();
    setInterfaceFilter('all');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: IDeploymentData) => row.version,
        id: 'version',
        header: 'Version',
        enableSorting: false,
        cell: ({ row }: { row: { original: IDeploymentData } }) => (
          <span className="text-gray-800 font-medium">
            {row.original.version}
          </span>
        ),
        meta: { className: 'min-w-[120px]' },
      },
      {
        accessor: (row: IDeploymentData) => row.interface_type,
        id: 'interface_type',
        header: 'Interface',
        enableSorting: false,
        cell: ({ row }: { row: { original: IDeploymentData } }) => (
          <span className="text-gray-800 font-normal">
            {getInterfaceLabel(row.original.interface_type as number)}
          </span>
        ),
        meta: { className: 'min-w-[140px]' },
      },
      {
        accessor: (row: IDeploymentData) => row.deployed_at,
        id: 'deployed_at',
        header: 'Deployed At',
        enableSorting: false,
        cell: ({ row }: { row: { original: IDeploymentData } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDateTime(new Date(row.original.deployed_at))}
          </span>
        ),
        meta: { className: 'min-w-[180px]' },
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IDeploymentData } }) => {
          const status = row.original.metadata?.status;

          let statusText = 'Pending';
          let statusClass =
            'text-yellow-600 bg-yellow-50 dark:bg-yellow-800 contrast:bg-yellow-800 dark:text-yellow-50 contrast:text-yellow-50';

          if (status === ManualDeploymentStatus.CONFIRMED) {
            statusText = 'Confirmed';
            statusClass =
              'text-green-600 bg-green-50 dark:bg-green-800 contrast:bg-green-800 dark:text-green-50 contrast:text-green-50';
          } else if (status === ManualDeploymentStatus.FAILED) {
            statusText = 'Declined';
            statusClass =
              'text-red-600 bg-red-50 dark:bg-red-800 contrast:bg-red-800 dark:text-red-50 contrast:text-red-50';
          }

          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusClass}`}
            >
              {statusText}
            </span>
          );
        },
        meta: { className: 'min-w-[120px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IDeploymentData } }) => {
          const status = row.original.metadata?.status;
          const isPending =
            !status ||
            (status !== ManualDeploymentStatus.CONFIRMED &&
              status !== ManualDeploymentStatus.FAILED);

          return (
            <div className="flex items-center gap-2">
              {hasPermission('deployment_management_read') && (
                <button
                  type="button"
                  onClick={() => handleViewClick(row.original.id)}
                  className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="View Details"
                  aria-label={`View deployment ${row.original.version}`}
                  disabled={isConfirming}
                >
                  <KeenIcon
                    type="filled"
                    icon="eye"
                    className="text-blue-500"
                  />
                </button>
              )}

              {isPending && hasPermission('deployment_management_update') && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      handleActionClick(row.original.id, 'confirm')
                    }
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-green-500"
                    title="Confirm Deployment"
                    aria-label={`Confirm deployment ${row.original.version}`}
                    disabled={isConfirming}
                  >
                    <KeenIcon
                      type="filled"
                      icon="check"
                      className="text-green-500"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleActionClick(row.original.id, 'decline')
                    }
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-red-500"
                    title="Decline Deployment"
                    aria-label={`Decline deployment ${row.original.version}`}
                    disabled={isConfirming}
                  >
                    <KeenIcon
                      type="filled"
                      icon="cross"
                      className="text-red-500"
                    />
                  </button>
                </>
              )}
            </div>
          );
        },
        meta: { className: 'w-[200px]' },
      },
    ],
    [handleViewClick, isConfirming, hasPermission]
  );

  // Get current versions for each interface
  const getCurrentVersion = (
    data: { data?: IDeploymentData[] } | undefined
  ) => {
    if (!data?.data || data.data.length === 0) return null;
    return data.data[0]; // First item is the most recent deployment
  };

  const currentAdminVersion = getCurrentVersion(adminData);
  const currentFrontendVersion = getCurrentVersion(frontendData);
  const currentBackendVersion = getCurrentVersion(backendData);

  const getStatusColor = (deployment: IDeploymentData | null) => {
    if (!deployment) return 'text-gray-400 contrast:text-white/70 ';
    const status = deployment.metadata?.status;

    if (status === ManualDeploymentStatus.CONFIRMED) return 'text-green-600';
    if (status === ManualDeploymentStatus.FAILED) return 'text-red-600';
    return 'text-yellow-600';
  };

  const getStatusText = (deployment: IDeploymentData | null) => {
    if (!deployment) return 'No Deployment';
    const status = deployment.metadata?.status;

    if (status === ManualDeploymentStatus.CONFIRMED) return 'Confirmed';
    if (status === ManualDeploymentStatus.FAILED) return 'Declined';
    return 'Pending';
  };

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading
            id="deployment-management-heading"
            title="Deployment Management"
            description="Monitor and manage deployment versions across all interfaces"
          />
        </Toolbar>
      </Container>
      <Container>
        <div className="space-y-6">
          {/* Current Version Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Admin Frontend Card */}
            <div className="bg-gray-100 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-700">
                  Admin Frontend
                </h2>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Current Version</span>
                  <span className="font-bold text-gray-900 text-lg">
                    {currentAdminVersion?.version || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status</span>
                  <span
                    className={`font-semibold ${getStatusColor(currentAdminVersion)}`}
                  >
                    {getStatusText(currentAdminVersion)}
                  </span>
                </div>
                {currentAdminVersion && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Deployed</span>
                    <span className="text-sm text-gray-500">
                      {formatDisplayDate(
                        new Date(currentAdminVersion.deployed_at)
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Frontend Card */}
            <div className="bg-gray-100 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-700">
                  Frontend
                </h2>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Current Version</span>
                  <span className="font-bold text-gray-900 text-lg">
                    {currentFrontendVersion?.version || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status</span>
                  <span
                    className={`font-semibold ${getStatusColor(currentFrontendVersion)}`}
                  >
                    {getStatusText(currentFrontendVersion)}
                  </span>
                </div>
                {currentFrontendVersion && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Deployed</span>
                    <span className="text-sm text-gray-500">
                      {formatDisplayDate(
                        new Date(currentFrontendVersion.deployed_at)
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Backend Card */}
            <div className="bg-gray-100 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-700">Backend</h2>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Current Version</span>
                  <span className="font-bold text-gray-900 text-lg">
                    {currentBackendVersion?.version || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status</span>
                  <span
                    className={`font-semibold ${getStatusColor(currentBackendVersion)}`}
                  >
                    {getStatusText(currentBackendVersion)}
                  </span>
                </div>
                {currentBackendVersion && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Deployed</span>
                    <span className="text-sm text-gray-500">
                      {formatDisplayDate(
                        new Date(currentBackendVersion.deployed_at)
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Deployments Table */}
          <div className="card card-grid min-w-full">
            <div className="card-header flex flex-wrap items-center gap-4 sm:gap-4 lg:gap-6 justify-between">
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                <div className="flex-shrink-0">
                  <label
                    className="input input-sm h-8 sm:h-10 flex items-center"
                    htmlFor="search-deployments"
                    aria-label="Search deployments by version"
                  >
                    <KeenIcon
                      type="filled"
                      icon="magnifier"
                      className="text-gray-500"
                    />
                    <input
                      id="search-deployments"
                      placeholder="Search deployments..."
                      type="text"
                      value={searchTerm}
                      onChange={handleSearch}
                      className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                      aria-label="Search deployments by version"
                    />
                  </label>
                </div>
              </div>

              <div className="flex items-end gap-2    ">
                <div className="flex-shrink-0 flex flex-col sm:flex-row items-center sm:gap-4">
                  <label
                    htmlFor="interface-filter"
                    className="block text-sm font-medium text-nowrap   text-gray-700 mb-1"
                  >
                    Filter by Interface
                  </label>
                  <select
                    id="interface-filter"
                    value={interfaceFilter}
                    onChange={(e) => {
                      setInterfaceFilter(e.target.value);
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-[30px]"
                    aria-label="Filter deployments by interface type"
                    disabled={isLoading || isFetching || isConfirming}
                  >
                    <option value="all">All Interfaces</option>
                    <option value="admin">Admin</option>
                    <option value="frontend">Frontend</option>
                    <option value="backend">Backend</option>
                  </select>
                </div>
                {(searchTerm || interfaceFilter !== 'all') && (
                  <button
                    className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={resetFilters}
                    aria-label="Clear all filters"
                    title="Clear all filters"
                    disabled={isLoading || isFetching || isConfirming}
                  >
                    <KeenIcon
                      type="solid"
                      icon="arrows-circle"
                      className="text-gray-600"
                    />
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            <div className="card-body">
              {isLoading || isFetching || isConfirming ? (
                <div
                  className="text-center py-4"
                  role="status"
                  aria-live="polite"
                >
                  <KeenIcon
                    type="filled"
                    icon="spinner"
                    className="text-gray-500 text-2xl animate-spin"
                  />
                  <span className="ml-2 text-gray-700">
                    Loading deployments...
                  </span>
                </div>
              ) : isError ? (
                <div className="text-center py-4 text-red-600" role="alert">
                  Error loading deployments. Please try again.
                </div>
              ) : (
                <>
                  {!isLoading && !isError && filteredData.length === 0 && (
                    <div className="text-center py-4">
                      <KeenIcon
                        type="filled"
                        icon="database"
                        className="text-gray-400 text-4xl mx-auto mb-2"
                      />
                      <p className="text-gray-500">No deployments found</p>
                    </div>
                  )}
                  {!isLoading && !isError && filteredData.length > 0 && (
                    <DataGrid
                      data-testid="deployments-data-grid"
                      columns={columns}
                      data={filteredData}
                      rowSelect={false}
                      loading={isLoading || isFetching || isConfirming}
                      pagination={{
                        page: pagination.pageIndex,
                        size: pagination.pageSize,
                        totalItems: data?.totalItems || totalItems,
                        totalPages: data?.totalPages || totalPages,
                        setPagination,
                        info: `{from} - {to} of {count}`,
                        sizes: [5, 10, 25, 50, 100],
                        sizesLabel: 'Show',
                        sizesDescription: 'per page',
                      }}
                      sorting={[{ id: 'deployed_at', desc: true }]}
                      serverSide={true}
                      hasActiveFilters={
                        !!searchTerm || interfaceFilter !== 'all'
                      }
                      aria-label="Deployments table"
                    />
                  )}
                </>
              )}

              <ConfirmationDialog
                open={confirmActionState.isOpen}
                onClose={() =>
                  setConfirmActionState({
                    isOpen: false,
                    deploymentId: null,
                    action: null,
                    release_notes: undefined,
                    release_notes_url: undefined,
                    attachment: null,
                  })
                }
                onConfirm={handleConfirmAction}
                disabled={isConfirming}
                message={
                  confirmActionState.action === 'confirm' ? (
                    <div className="space-y-3">
                      <p className="text-gray-700">
                        Are you sure you want to confirm this deployment as
                        successful?
                      </p>
                      <div>
                        <label
                          htmlFor="release_notes"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Release Notes (optional)
                        </label>
                        <textarea
                          id="release_notes"
                          name="release_notes"
                          placeholder="Enter release notes..."
                          value={confirmActionState.release_notes || ''}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                          disabled={isConfirming}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="attachment"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Release Notes File (optional)
                        </label>
                        <input
                          id="attachment"
                          type="file"
                          name="attachment"
                          accept=".pdf,.doc,.docx,.txt"
                          onChange={handleFileChange}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          disabled={isConfirming}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="release_notes_url"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Release Notes URL (optional)
                        </label>
                        <input
                          id="release_notes_url"
                          type="url"
                          name="release_notes_url"
                          placeholder="https://example.com/release-notes"
                          value={confirmActionState.release_notes_url || ''}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          disabled={isConfirming}
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  ) : confirmActionState.action === 'decline' ? (
                    <div className="space-y-3">
                      <p className="text-gray-700">
                        Are you sure you want to decline this deployment?
                      </p>
                    </div>
                  ) : null
                }
                cancelButtonText="Cancel"
                confirmButtonText={
                  confirmActionState.action === 'confirm'
                    ? 'Confirm'
                    : confirmActionState.action === 'decline'
                      ? 'Decline'
                      : 'Confirm'
                }
              />
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default DeploymentsListing;
